import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";
import { useUserBlocks } from "@/hooks/useUserBlocks";
import { Link } from "react-router-dom";

const REASONS = [
  { value: "harassment", label: "Harassment, bullying or abuse" },
  { value: "sexual", label: "Sexual content" },
  { value: "hate", label: "Hate or discrimination" },
  { value: "illegal", label: "Illegal or dangerous" },
  { value: "child_safety", label: "Child safety concern" },
  { value: "spam", label: "Spam or scam" },
  { value: "fake_profile", label: "Fake profile" },
  { value: "inappropriate", label: "Other inappropriate content" },
  { value: "other", label: "Other" },
];

export type ReportContext = "profile" | "circle" | "playdate" | "dm";

interface ReportUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportedUserId: string;
  reportedUserName: string;
  /** Where the report came from; defaults to a profile report. */
  context?: ReportContext;
  /** The reported chat message, if reporting a single message. */
  messageId?: string;
  messageText?: string;
  /** Circle / meet-up / conversation id the message lives in. */
  contextId?: string;
}

export const ReportUserDialog = ({
  open,
  onOpenChange,
  reportedUserId,
  reportedUserName,
  context = "profile",
  messageId,
  messageText,
  contextId,
}: ReportUserDialogProps) => {
  const { user } = useAuth();
  const { blockUser, isBlocked } = useUserBlocks();
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!user || !reason) return;

    setSubmitting(true);
    try {
      const { data: inserted, error } = await (supabase.from("user_reports" as any).insert({
        reporter_id: user.id,
        reported_user_id: reportedUserId,
        reason,
        message: message.trim() || null,
        context_type: context,
        message_id: messageId ?? null,
        context_id: contextId ?? null,
        message_snapshot: messageText ? messageText.slice(0, 4000) : null,
      } as any).select("id").single() as any);

      if (error) {
        if (error.code === "23505") {
          toast.info(messageId ? "You have already reported this message" : "You have already reported this user");
        } else {
          throw error;
        }
      } else {
        if (inserted?.id) {
          supabase.functions
            .invoke("notify-admins-report", { body: { reportId: inserted.id } })
            .catch((e) => console.error("notify-admins-report failed:", e));
        }
        toast.success("Thanks — our team will review this.", {
          description: "You can also block them so they can't contact you.",
          action: isBlocked(reportedUserId)
            ? undefined
            : {
                label: "Block",
                onClick: () => {
                  blockUser(reportedUserId);
                },
              },
          duration: 8000,
        });
      }

      onOpenChange(false);
      setReason("");
      setMessage("");
    } catch (err) {
      console.error("Error reporting user:", err);
      toast.error("Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-destructive" />
            {messageId ? `Report message from ${reportedUserName}` : `Report ${reportedUserName}`}
          </DialogTitle>
          <DialogDescription>
            Let us know what's wrong. Our team will review this report privately — {reportedUserName} won't be told who reported them.
          </DialogDescription>
        </DialogHeader>
        {messageText && (
          <blockquote className="rounded-md border-l-4 border-destructive/50 bg-muted px-3 py-2 text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap break-words">
            {messageText}
          </blockquote>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Details (optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us more about what happened..."
              maxLength={500}
              rows={3}
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          See our{" "}
          <Link to="/community-rules" className="text-primary underline" onClick={() => onOpenChange(false)}>
            Community Rules
          </Link>
          . If someone is in immediate danger, call 999.
        </p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!reason || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Submit Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
